import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Field, Money, Note, PageHeader, Panel, Spinner } from '../ui';
import {
  KEYS, changed, parseBands, type Key, type Policy, type RecallTrigger,
} from './portal-settings';

/**
 * A-36 Portal settings — SC-07. The rules a portal runs on, and the one place they change.
 *
 * Two halves, deliberately unlike each other:
 *
 *   FACTS   currency, timezone, selling entity, capabilities, ring sizes. Shown, never
 *           edited here: a portal does not stop being a consignment portal because a box
 *           was unticked, and its currency is frozen into every value it has recorded.
 *   POLICY  the editable settings. Two of them move money (invariant 2), so a change is
 *           reviewed before it is saved — what it changes, when that takes effect, and
 *           what it touches today — and is saved with a reason, which goes to the audit log.
 *
 * Reading needs `portal.view`; changing needs `system.config`. Every value is shown as the
 * portal's own or the platform default, because "the portal says 14 days" and "nobody has
 * decided, so 14 days applies" are different answers to give a client.
 */

interface View {
  portal: { id: string; code: string; name: string };
  facts: { currency: string; timezone: string; sellingEntityCountry: string; capabilities: Record<string, boolean>; sizeScale: string[] };
  policy: { [K in Key]: { value: Policy[K]; source: 'portal' | 'default' } };
  effect: Record<Key, string>;
  impact: { soldUnpaid: Array<{ currency: string; pieces: number; wholesaleValue: string }>; retailersOnDefaultBudget: number };
  version: string;
}

/** Label and requirement for each policy, in the order the screen reads. */
const META: Record<Key, { label: string; ref: string }> = {
  defaultBudget: { label: 'Default inventory budget', ref: 'BG-01' },
  budgetVariesPerRetailer: { label: 'Budget may be set per retailer', ref: 'BG-07, D63' },
  budgetTolerancePct: { label: 'Tolerance over budget', ref: 'BG-04' },
  budgetReleaseOn: { label: 'A sold piece frees budget', ref: 'BG-06, D53' },
  payableDueRule: { label: 'Payable due rule', ref: 'FN-05, FN-11, D64' },
  taxTreatment: { label: 'Tax treatment', ref: 'FN-17, D76' },
  leadTime: { label: 'Manufacturing lead time', ref: 'MF-02' },
  standardSpecification: { label: 'Standard specification', ref: 'PC-04b, D61' },
  slowMovingDays: { label: 'Slow-moving after', ref: 'AN-01' },
  priceBands: { label: 'Price bands for sales reports', ref: 'AN-03' },
  reportingQuarterStartMonth: { label: 'Reporting quarter starts in', ref: 'AN-13, D98' },
  recallTrigger: { label: 'What an ageing piece triggers', ref: 'CN-09, D20' },
  lossDefaultOutcome: { label: 'Outcome offered first for a lost piece', ref: 'CN-10, D39, D81' },
  verificationCycleDays: { label: 'Stock count cycle', ref: 'CN-11, D40' },
};

/**
 * AN-13 — the months, for the reporting-quarter setting. A month is a name to the Admin reading
 * A-36, not a number, and the span is spelled out because "quarters start in April" and
 * "Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar" are not equally easy to check.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const QUARTER_SPAN = (startMonth: number): string => [0, 3, 6, 9]
  .map((o) => `${MONTHS[(startMonth - 1 + o) % 12]!.slice(0, 3)}–${MONTHS[(startMonth + 1 + o) % 12]!.slice(0, 3)}`)
  .join(', ');

const OUTCOME: Record<NonNullable<Policy['lossDefaultOutcome']>, string> = {
  CHARGED: 'Charged to the retailer at wholesale value', INSURANCE_CLAIM: 'Insurance claim', WRITTEN_OFF: 'Written off',
};

const CAPABILITY: Record<string, string> = { memo: 'Consignment', subscriptions: 'Annual subscription', creditLimits: 'Credit limits (retired at v1.6)', buyingGroup: 'Buying group on applications' };

function describe(k: Key, p: Policy, currency: string): ReactNode {
  switch (k) {
    case 'defaultBudget':
      return p.defaultBudget === null ? 'None — every retailer is given their own' : <Money amount={p.defaultBudget} currency={currency} />;
    case 'budgetVariesPerRetailer':
      return p.budgetVariesPerRetailer ? 'Yes — with a reason, under budget.override' : 'No — every retailer has the default';
    case 'budgetTolerancePct':
      /* BG-04 — a percentage, not an amount: shown as typed, never through <Money>. Zero is a
         real setting on this portal and says so rather than showing a bare "0%". */
      return Number(p.budgetTolerancePct) === 0
        ? 'None — an order over the available budget is refused'
        : `${p.budgetTolerancePct}% over the available budget is still accepted`;
    case 'budgetReleaseOn':
      return p.budgetReleaseOn === 'SALE' ? 'When the sale is reported' : 'When Admin confirms payment';
    case 'payableDueRule':
      return p.payableDueRule.type === 'NET_DAYS'
        ? `${p.payableDueRule.days} days after the invoice`
        : `Month end, plus ${p.payableDueRule.graceDays} days' grace`;
    case 'taxTreatment':
      return 'Other — manual: Admin enters the tax and its basis on each invoice';
    case 'leadTime':
      return `${p.leadTime.minDays}–${p.leadTime.maxDays} days`;
    case 'standardSpecification':
      return p.standardSpecification === null ? <span className="mk-dim">Not set — open decision D61</span>
        : `Diamond ${p.standardSpecification.diamond}; gold ${p.standardSpecification.gold}`;
    case 'slowMovingDays':
      return `${p.slowMovingDays} days held with no sale`;
    case 'priceBands':
      return `${p.priceBands.join(' · ')} (${currency}, wholesale)`;
    case 'reportingQuarterStartMonth':
      /* AN-13 — which three-month blocks this portal calls its quarters. Named by month rather
         than shown as a number, because "4" is not an answer anyone can check. */
      return `${MONTHS[p.reportingQuarterStartMonth - 1] ?? ''} — quarters run ${QUARTER_SPAN(p.reportingQuarterStartMonth)}`;
    case 'recallTrigger':
      return p.recallTrigger.type === 'NONE' ? <span className="mk-dim">Nothing — open decision D20. Admin recalls by hand.</span>
        : p.recallTrigger.type === 'ALERT' ? `Listed as due for recall after ${p.recallTrigger.afterDays} days held; a person decides`
          : `Recalled automatically after ${p.recallTrigger.afterDays} days held`;
    case 'lossDefaultOutcome':
      return p.lossDefaultOutcome === null ? <span className="mk-dim">None — open decision D81. Admin chooses each time.</span>
        : `${OUTCOME[p.lossDefaultOutcome]} — offered first; Admin still chooses`;
    case 'verificationCycleDays':
      return p.verificationCycleDays === null ? <span className="mk-dim">No cycle — open decision D40. Counted on demand.</span>
        : `Every ${p.verificationCycleDays} days`;
  }
}

function Row({ label, refs, children, aside }: { label: string; refs?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--mk-space-4)', padding: 'var(--mk-space-2) 0', borderBottom: '1px solid var(--mk-line)', flexWrap: 'wrap' }}>
      <div style={{ flex: '0 0 16rem' }}>
        <div>{label}</div>
        {refs ? <span className="mk-hint">{refs}</span> : null}
      </div>
      <div style={{ flex: '1 1 20rem' }}>{children}</div>
      {aside ? <div>{aside}</div> : null}
    </div>
  );
}

export function PortalSettings() {
  const { portal, can } = useSession();
  const qc = useQueryClient();
  const key = ['portal', portal?.code ?? null, 'settings'];
  const { data: view, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get<View>('/portal-settings') });
  const [draft, setDraft] = useState<Policy | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<{ text: string; stale: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Spinner />;
  if (error || !view) return <Alert tone="stop">{error instanceof Error ? error.message : 'Could not load the settings.'}</Alert>;

  const saved = Object.fromEntries(KEYS.map((k) => [k, view.policy[k].value])) as unknown as Policy;
  const mayEdit = can('system.config');
  const moved = draft ? changed(saved, draft) : [];
  const set = <K extends Key>(k: K, v: Policy[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  const save = () => {
    if (!draft) return;
    setBusy(true); setFailure(null);
    void (async () => {
      try {
        const next = await api.put<View>('/portal-settings', {
          settings: { ...draft, defaultBudget: draft.defaultBudget?.trim() || null },
          reason, basedOn: view.version,
        });
        qc.setQueryData(key, next);
        setDraft(null); setReviewing(false); setReason('');
      } catch (e) {
        setFailure({ text: e instanceof ApiError ? e.message : 'The settings were not saved.', stale: e instanceof ApiError && e.status === 409 });
      } finally { setBusy(false); }
    })();
  };

  return (
    <>
      <PageHeader
        title="Portal settings"
        /*
         * Just the portal. "Settings, not market rules: nothing here is decided by which
         * country the portal serves" is D46 — a rule about how this platform is BUILT, aimed at
         * the next developer, and it was being shown to an administrator who has no decision to
         * make about it. It is stated where it binds: CLAUDE.md, and the PortalContext that
         * refuses a country check. The Note below carries what the administrator does need.
         */
        sub={view.portal.name}
        actions={mayEdit && !draft ? <Button onClick={() => setDraft(saved)}>Change settings</Button> : null}
      />
      <Note>
        These settings are stored and audited here. The budget, payables and ordering modules read them as each is
        built — until then a change is recorded, and takes effect where it is read.
      </Note>

      <Panel title="Facts about this portal" pad>
        <Row label="Currency" refs="SC-07">{view.facts.currency} <span className="mk-hint">— every amount on this portal is in it, so it is never edited</span></Row>
        <Row label="Business timezone" refs="D49">{view.facts.timezone} <span className="mk-hint">— ageing, due dates and renewals count days here</span></Row>
        <Row label="Selling entity" refs="SC-04a">{view.facts.sellingEntityCountry}</Row>
        <Row label="Capabilities" refs="SC-04">
          {Object.entries(view.facts.capabilities).map(([c, on]) => (
            <span key={c} style={{ marginRight: 'var(--mk-space-2)' }}><Chip tone={on ? 'done' : 'lapse'}>{CAPABILITY[c] ?? c}: {on ? 'offered' : 'not offered'}</Chip></span>
          ))}
          <div className="mk-hint">Deployment facts, not switches. A portal does not stop being a consignment portal because a box was unticked.</div>
        </Row>
        <Row label="Sizes offered" refs="PC-04a, D60">
          {view.facts.sizeScale.length ? view.facts.sizeScale.join(' · ') : <span className="mk-dim">No sizes offered</span>}
          <div className="mk-hint">
            Every size switched on for this portal under Collections &amp; options (A-10), for all articles — the ring
            scale SC-07 names is part of this list. Shown here, kept there.
          </div>
        </Row>
      </Panel>

      <Panel title="Policy" pad>
        {!draft ? KEYS.map((k) => (
          <Row key={k} label={META[k].label} refs={META[k].ref}
            aside={<Chip tone={view.policy[k].source === 'portal' ? 'us' : 'lapse'}>{view.policy[k].source === 'portal' ? 'set for this portal' : 'platform default'}</Chip>}>
            {describe(k, saved, view.facts.currency)}
            <div className="mk-hint">{view.effect[k]}</div>
          </Row>
        )) : (
          <PolicyForm draft={draft} set={set} currency={view.facts.currency} />
        )}
      </Panel>

      {draft ? (
        <Panel title="Review and save" pad>
          {moved.length === 0 ? <Note>Nothing has changed yet.</Note> : (
            <>
              {moved.map((k) => (
                <Row key={k} label={META[k].label} refs={META[k].ref}>
                  <div><span className="mk-dim">{describe(k, saved, view.facts.currency)}</span> → <b>{describe(k, draft, view.facts.currency)}</b></div>
                  <Alert tone="them">{view.effect[k]}</Alert>
                  {k === 'budgetReleaseOn' ? (
                    <Note>
                      {view.impact.soldUnpaid.length === 0
                        ? 'No piece on this portal is sold and unpaid today, so no retailer’s budget moves.'
                        : view.impact.soldUnpaid.map((u) => (
                          <div key={u.currency}>
                            {u.pieces} sold, unpaid pieces worth <Money amount={u.wholesaleValue} currency={u.currency} />{' '}
                            {draft.budgetReleaseOn === 'SALE' ? 'leave budget in use at once.' : 'count against budget again at once — a retailer pushed over budget cannot order until under it (BG-08).'}
                          </div>
                        ))}
                    </Note>
                  ) : null}
                  {k === 'defaultBudget' ? <Note>{view.impact.retailersOnDefaultBudget} retailers on this portal have no individual budget and take the default.</Note> : null}
                </Row>
              ))}
              <Field label="Why" hint="Required. Recorded in the audit log with your name — this is money policy.">
                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              {reviewing ? (
                <Alert tone="stop" actions={<>
                  <Button disabled={busy} onClick={save}>Save {moved.length} change{moved.length === 1 ? '' : 's'}</Button>{' '}
                  <Button tone="ghost" onClick={() => setReviewing(false)}>Back</Button>
                </>}>
                  Saving applies each change as described above, to every retailer on {view.portal.name}.
                </Alert>
              ) : (
                <Button disabled={reason.trim().length < 10} onClick={() => setReviewing(true)}>Review and save</Button>
              )}
            </>
          )}
          {failure ? (
            <Alert tone="stop" actions={failure.stale ? <Button tone="ghost" onClick={() => { setDraft(null); setReviewing(false); void qc.invalidateQueries({ queryKey: key }); }}>Reload</Button> : null}>
              {failure.text}
            </Alert>
          ) : null}
          <div style={{ marginTop: 'var(--mk-space-3)' }}>
            <Button tone="ghost" onClick={() => { setDraft(null); setReviewing(false); setFailure(null); }}>Discard changes</Button>
          </div>
        </Panel>
      ) : null}
    </>
  );
}

function PolicyForm({ draft, set, currency }: { draft: Policy; set: <K extends Key>(k: K, v: Policy[K]) => void; currency: string }) {
  const rule = draft.payableDueRule;
  return (
    <div className="mk-fset">
      <Field label={`${META.defaultBudget.label} (${currency})`} hint="Wholesale value. Leave empty for no portal default — that is not the same as zero.">
        {/* A string, never a number (NF-04): the server validates and stores it to four places. */}
        <input inputMode="decimal" value={draft.defaultBudget ?? ''} onChange={(e) => set('defaultBudget', e.target.value === '' ? null : e.target.value)} />
      </Field>
      <Field label={META.budgetVariesPerRetailer.label} hint="Required when there is no default — otherwise no retailer would have a budget.">
        <select value={draft.budgetVariesPerRetailer ? 'yes' : 'no'} onChange={(e) => set('budgetVariesPerRetailer', e.target.value === 'yes')}>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </Field>
      <Field
        label={`${META.budgetTolerancePct.label} (%)`}
        hint="How far over its available budget an order may still go through. A retailer given its own tolerance on A-04 is unaffected by this. Enter 0 for none."
      >
        {/* A string, never a number (NF-04): it multiplies money, and the server stores it to four places. */}
        <input inputMode="decimal" value={draft.budgetTolerancePct} onChange={(e) => set('budgetTolerancePct', e.target.value)} />
      </Field>
      <Field label={META.budgetReleaseOn.label}>
        <select value={draft.budgetReleaseOn} onChange={(e) => set('budgetReleaseOn', e.target.value as Policy['budgetReleaseOn'])}>
          <option value="PAYMENT">When Admin confirms payment</option>
          <option value="SALE">When the sale is reported</option>
        </select>
      </Field>
      <Field label={META.payableDueRule.label}>
        <select
          value={rule.type}
          onChange={(e) => set('payableDueRule', e.target.value === 'NET_DAYS' ? { type: 'NET_DAYS', days: 14 } : { type: 'MONTH_END', graceDays: 7 })}
        >
          <option value="NET_DAYS">A number of days after the invoice</option>
          <option value="MONTH_END">Month end, plus grace days</option>
        </select>
      </Field>
      <Field label={rule.type === 'NET_DAYS' ? 'Days after the invoice' : 'Grace days after month end'}>
        <input
          type="number" min={0} max={rule.type === 'NET_DAYS' ? 365 : 28}
          value={rule.type === 'NET_DAYS' ? rule.days : rule.graceDays}
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10) || 0;
            set('payableDueRule', rule.type === 'NET_DAYS' ? { type: 'NET_DAYS', days: n } : { type: 'MONTH_END', graceDays: n });
          }}
        />
      </Field>
      <Field label={META.taxTreatment.label} hint="The only treatment in v1 (D76). The portal holds no rates and computes no tax.">
        <input value="Other — manual" disabled />
      </Field>
      <Field label="Shortest lead time (days)">
        <input type="number" min={1} max={365} value={draft.leadTime.minDays}
          onChange={(e) => set('leadTime', { ...draft.leadTime, minDays: Number.parseInt(e.target.value, 10) || 1 })} />
      </Field>
      <Field label="Longest lead time (days)">
        <input type="number" min={1} max={365} value={draft.leadTime.maxDays}
          onChange={(e) => set('leadTime', { ...draft.leadTime, maxDays: Number.parseInt(e.target.value, 10) || 1 })} />
      </Field>
      <Field label="Standard diamond specification" hint="Shown on every product, never selected (PC-04b). Leave both empty while D61 is open.">
        <input
          value={draft.standardSpecification?.diamond ?? ''} placeholder="VVS–VS clarity, EF colour"
          onChange={(e) => set('standardSpecification', e.target.value === '' && !draft.standardSpecification?.gold ? null
            : { diamond: e.target.value, gold: draft.standardSpecification?.gold ?? '' })}
        />
      </Field>
      <Field label={META.recallTrigger.label} hint="D20 is open. Nothing is the default: no piece is recalled or flagged until this is set.">
        <select
          value={draft.recallTrigger.type}
          onChange={(e) => {
            const days = draft.recallTrigger.type === 'NONE' ? 180 : draft.recallTrigger.afterDays;
            const type = e.target.value as RecallTrigger['type'];
            set('recallTrigger', type === 'NONE' ? { type } : { type, afterDays: days });
          }}
        >
          <option value="NONE">Nothing — Admin recalls by hand</option>
          <option value="ALERT">List as due for recall on Returns &amp; Stock</option>
          <option value="RECALL">Recall automatically, every day</option>
        </select>
      </Field>
      {draft.recallTrigger.type !== 'NONE' ? (
        <Field label="Days held before a piece is due">
          <input type="number" min={1} max={3650} value={draft.recallTrigger.afterDays}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10) || 1;
              set('recallTrigger', draft.recallTrigger.type === 'ALERT' ? { type: 'ALERT', afterDays: n } : { type: 'RECALL', afterDays: n });
            }} />
        </Field>
      ) : null}
      <Field label={META.lossDefaultOutcome.label} hint="D39 and D81 are open. Offered first only; Admin still chooses every outcome.">
        <select value={draft.lossDefaultOutcome ?? ''} onChange={(e) => set('lossDefaultOutcome', e.target.value === '' ? null : e.target.value as NonNullable<Policy['lossDefaultOutcome']>)}>
          <option value="">None</option>
          {(Object.keys(OUTCOME) as Array<keyof typeof OUTCOME>).map((o) => <option key={o} value={o}>{OUTCOME[o]}</option>)}
        </select>
      </Field>
      <Field label={`${META.verificationCycleDays.label} (days)`} hint="D40 is open. Leave empty for no cycle — counts are run when someone asks.">
        <input type="number" min={7} max={730} value={draft.verificationCycleDays ?? ''}
          onChange={(e) => set('verificationCycleDays', e.target.value === '' ? null : Number.parseInt(e.target.value, 10) || 7)} />
      </Field>
      <Field label="Standard gold specification">
        <input
          value={draft.standardSpecification?.gold ?? ''}
          onChange={(e) => set('standardSpecification', e.target.value === '' && !draft.standardSpecification?.diamond ? null
            : { diamond: draft.standardSpecification?.diamond ?? '', gold: e.target.value })}
        />
      </Field>
      <Field label={META.reportingQuarterStartMonth.label}
        hint="Which three-month blocks Analytics calls this portal's quarters. D98 is open; calendar quarters apply until it is answered. Changing it re-cuts every quarter, past ones included — nothing per quarter is stored.">
        <select value={draft.reportingQuarterStartMonth}
          onChange={(e) => set('reportingQuarterStartMonth', Number.parseInt(e.target.value, 10) || 1)}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </Field>
      <Field label={`${META.slowMovingDays.label} (days)`} hint="A product is listed as slow-moving when a piece of it is held longer than this, with no sale in the period.">
        <input type="number" min={1} max={730} value={draft.slowMovingDays}
          onChange={(e) => set('slowMovingDays', Number.parseInt(e.target.value, 10) || 1)} />
      </Field>
      <BandsField bands={draft.priceBands} currency={currency} onChange={(b) => set('priceBands', b)} />
    </div>
  );
}

/**
 * AN-03 — the band boundaries as Admin types them. Held as text while typing, so a trailing comma
 * survives the keystroke; the list it parses to is what is saved, and the server checks the order.
 */
function BandsField({ bands, currency, onChange }: { bands: string[]; currency: string; onChange: (b: string[]) => void }) {
  const [text, setText] = useState(bands.join(', '));
  return (
    <Field label={`${META.priceBands.label} (${currency})`} hint="Whole wholesale amounts, lowest first, separated by commas: 500, 1000, 2500 groups sales under 500, 500–1000, 1000–2500 and 2500+.">
      <input value={text} onChange={(e) => { setText(e.target.value); onChange(parseBands(e.target.value)); }} />
    </Field>
  );
}
